<template>
  <div class="dark-theme-wrapper">
    <div class="container py-5">
      <div class="text-center mb-5">
        <h1 class="main-title">ŽEBŘÍČEK HRÁČŮ</h1>
        <div class="separator mx-auto"></div>
      </div>

      <div v-if="loading" class="text-center text-muted my-5">
        <div class="spinner-border text-danger" role="status"></div>
        <p class="mt-2">Načítám pořadí...</p>
      </div>

      <div v-else class="leaderboard-card">
        <div class="table-responsive">
          <table class="custom-table">
            <thead>
              <tr>
                <th class="text-center rank-col">#</th>
                <th class="text-start player-col">HRÁČ</th>
                <th class="text-center joker-col">JOKER</th>
                <th class="text-center round-col text-red">
                  {{ lastActiveRound > 0 ? lastActiveRound + ". KOLO" : "-" }}
                </th>
                <th class="text-end points-col">CELKEM</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(player, index) in leaderboard"
                :key="player.id"
                :class="{ 'top-three': index < 3 }"
              >
                <td class="text-center rank-cell">
                  <div v-if="index === 0" class="medal gold">
                    <svg
                      class="medal-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      ></path>
                    </svg>
                  </div>
                  <div v-else-if="index === 1" class="medal silver">
                    <svg
                      class="medal-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      ></path>
                    </svg>
                  </div>
                  <div v-else-if="index === 2" class="medal bronze">
                    <svg
                      class="medal-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      ></path>
                    </svg>
                  </div>
                  <span v-else class="rank-number">{{ index + 1 }}.</span>
                </td>

                <td class="text-start player-cell">
                  <div class="d-flex align-items-center">
                    <img
                      v-if="player.profilePicture"
                      :src="player.profilePicture"
                      class="table-avatar me-3"
                      alt="Avatar"
                    />
                    <div v-else class="table-avatar-placeholder me-3">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="width: 16px; height: 16px"
                      >
                        <path
                          d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                        ></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <span class="username">{{ player.username }}</span>
                  </div>
                </td>

                <td class="text-center joker-cell">
                  <div class="tooltip-container">
                    <svg
                      class="joker-icon"
                      :class="{
                        used: player.displayJokerUsed,
                        available: !player.displayJokerUsed,
                      }"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polygon
                        points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"
                      ></polygon>
                    </svg>
                  </div>
                </td>

                <td class="text-center round-score-cell">
                  <span class="round-points">
                    {{
                      player.rounds && player.rounds[lastActiveRound]
                        ? player.rounds[lastActiveRound]
                        : "-"
                    }}
                  </span>
                </td>

                <td class="text-end points-cell">
                  <span class="points">{{ player.totalPoints }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../auth";
import { collection, getDocs, query } from "firebase/firestore";

export default {
  name: "UserLeaderboard",
  data() {
    return {
      leaderboard: [],
      loading: true,
      lastActiveRound: 0,
    };
  },
  async mounted() {
    await this.fetchLeaderboard();
  },
  methods: {
    async fetchLeaderboard() {
      try {
        const usersQuery = query(collection(db, "users"));
        const querySnapshot = await getDocs(usersQuery);

        let maxRoundFound = 0;

        // 1. Zjistíme globálně, jaké je poslední VYHODNOCENÉ kolo (má nějaké body)
        querySnapshot.docs.forEach((doc) => {
          const rounds = doc.data().rounds || {};
          const userMaxRound = Object.keys(rounds).reduce((max, current) => {
            return Math.max(max, Number(current));
          }, 0);
          if (userMaxRound > maxRoundFound) {
            maxRoundFound = userMaxRound;
          }
        });

        this.lastActiveRound = maxRoundFound;

        const allUsers = querySnapshot.docs.map((doc) => {
          const userData = doc.data();
          const rounds = userData.rounds || {};

          // --- HLAVNÍ ZMĚNA: LOGIKA SKRÝVÁNÍ JOKERA ---
          const realJokerUsed = userData.jokerUsed || false;
          let displayJokerUsed = realJokerUsed;

          // Získáme číslo kola, ve kterém byl Joker použit (pokud existuje)
          const jokerUsedRound = userData.jokerUsedRound;

          if (realJokerUsed && jokerUsedRound) {
            // Pokud uživatel má body v tomto kole (rounds[číslo] existuje),
            // znamená to, že admin už kolo vyhodnotil -> Můžeme Jokera ukázat.
            // Pokud body chybí (undefined), kolo běží nebo čeká na vyhodnocení -> SKRÝT.
            if (rounds[jokerUsedRound] === undefined) {
              displayJokerUsed = false;
            }
          }
          // ---------------------------------------------

          return {
            id: doc.id,
            username: userData.username || "Neznámý hráč",
            profilePicture: userData.profilePicture || null,
            totalPoints: userData.totalPoints || 0,
            jokerUsed: realJokerUsed,
            displayJokerUsed: displayJokerUsed, // Tuto hodnotu používáme v HTML
            role: userData.role || "user",
            rounds: rounds,
          };
        });

        this.leaderboard = allUsers
          .filter((user) => user.role !== "admin")
          .sort((a, b) => b.totalPoints - a.totalPoints);

        this.loading = false;
      } catch (error) {
        console.error("Chyba při načítání leaderboardu:", error);
      }
    },
  },
};
</script>

<style scoped>
/* STYLY ZŮSTÁVAJÍ STEJNÉ, ZKOPÍRUJTE JE Z PŘEDCHOZÍ VERZE */
.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff;
  text-transform: uppercase;
}

.text-red {
  color: #dc3545;
}

.separator {
  width: 60px;
  height: 4px;
  background-color: #dc3545;
  border-radius: 2px;
}

.leaderboard-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  max-width: 800px; /* Užší max-width stačí */
  margin: 0 auto;
}

.table-responsive {
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  color: #ddd;
  white-space: nowrap;
}

.custom-table th {
  background-color: #0d0d0d;
  color: #888;
  font-size: 0.75rem; /* Menší font pro hlavičku */
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 15px 10px; /* Menší padding */
  border-bottom: 2px solid #333;
}

.custom-table td {
  padding: 15px 10px;
  border-bottom: 1px solid #222;
  vertical-align: middle;
}

.custom-table tr:last-child td {
  border-bottom: none;
}

.custom-table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

/* Avatar v tabulce */
.table-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #444;
}

.table-avatar-placeholder {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  border: 1px solid #333;
}

/* Sloupce - Optimalizace šířky */
.rank-col {
  width: 40px;
}
.player-col {
  width: auto;
} /* Zbytek místa pro jméno */
.joker-col {
  width: 50px;
}
.round-col {
  width: 80px;
}
.points-col {
  width: 70px;
}

/* Rank / Medaile */
.medal-icon {
  width: 24px;
  height: 24px;
  display: inline-block;
}
.medal.gold {
  color: #ffd700;
  filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5));
}
.medal.silver {
  color: #c0c0c0;
  filter: drop-shadow(0 0 5px rgba(192, 192, 192, 0.3));
}
.medal.bronze {
  color: #cd7f32;
  filter: drop-shadow(0 0 5px rgba(205, 127, 50, 0.3));
}
.rank-number {
  color: #666;
  font-weight: bold;
}

/* Jméno */
.username {
  font-weight: bold;
  font-size: 0.95rem;
  color: #fff;
}
.top-three .username {
  font-size: 1.05rem;
}

/* Body za kolo */
.round-score-cell {
  color: #aaa;
  font-weight: bold;
  font-size: 0.9rem;
}
.round-points {
  display: inline-block;
  background: #222;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #333;
}

/* Joker */
.joker-icon {
  width: 18px;
  height: 18px;
  transition: all 0.3s;
}
.joker-icon.available {
  stroke: #dc3545;
  fill: rgba(220, 53, 69, 0.2);
}
.joker-icon.used {
  stroke: #444;
  fill: none;
  opacity: 0.5;
}

/* Body Celkem */
.points {
  font-size: 1.1rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: 1px;
}
.top-three .points {
  color: #dc3545;
  font-size: 1.2rem;
}

/* Tooltip */
.tooltip-container {
  position: relative;
  display: inline-block;
}
</style>
